<template>
  <div class="border border-[#979797]/30 rounded-xl p-[9px] pb-[13px]">
    <div class="flex items-start justify-between gap-x-4 mb-2.5">
      <div class="min-w-0">
        <div class="text-[14px] leading-4">
          Track {{ formatNumber(index + 1) }}
        </div>

        <div
          class="mt-1 flex flex-col items-start gap-y-1 text-[12px] leading-4 text-gray-600 tablet-h:flex-row tablet-h:flex-wrap tablet-h:items-center tablet-h:gap-x-2 tablet-h:gap-y-0"
        >
          <span v-if="timeRange">{{ timeRange }}</span>
          <span v-if="timeRange && stemCount" class="hidden tablet-h:block"
            >•</span
          >
          <span v-if="stemCount" class="flex items-center gap-x-[5px]">
            {{ stemCount }} {{ stemCount === 1 ? 'stem' : 'stems' }}
            <span class="flex items-center gap-x-[3px] text-gray-500">
              <component
                :is="icon"
                v-for="(icon, iconIndex) in stemIcons"
                :key="iconIndex"
                class="size-[14px]"
              />
            </span>
          </span>
        </div>
      </div>

      <Button
        variant="stroke"
        size="md"
        class="shrink-0"
        :aria-label="isExpanded ? 'Hide details' : 'View details'"
        @click="isExpanded = !isExpanded"
      >
        <span class="flex items-center">
          <span
            class="grid transition-[grid-template-columns,margin] duration-200 ease-out"
            :class="
              isExpanded ? 'mr-0 grid-cols-[0fr]' : 'mr-[6px] grid-cols-[1fr]'
            "
          >
            <span
              class="overflow-hidden whitespace-nowrap transition-opacity duration-200 ease-out"
              :class="isExpanded && 'opacity-0'"
              >View details</span
            >
          </span>

          <ChevronDownIcon
            class="size-[14px] shrink-0 transition-transform duration-200 ease-out"
            :class="isExpanded && 'rotate-180'"
          />
        </span>
      </Button>
    </div>

    <div class="flex items-center gap-x-5">
      <div
        class="min-w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center cursor-pointer"
        @click="handleClick"
      >
        <component :is="playIcon" class="size-[18px]" :style="iconStyles" />
      </div>
      <div ref="el" class="w-full"></div>
    </div>

    <Transition
      @enter="handleEnter"
      @after-enter="handleAfterEnter"
      @leave="handleLeave"
    >
      <div
        v-if="isExpanded && hasContent"
        class="overflow-hidden transition-[height] duration-300 ease-out"
      >
        <template v-if="track.summary">
          <div class="mt-[22px]">
            <div class="text-[12px] text-gray-500 leading-[20px] mb-1.5">
              Summary
            </div>
            <div class="leading-[20px]">
              {{ track.summary }}
            </div>
          </div>
        </template>

        <template v-if="track.attributes.length">
          <div class="my-[22px] w-full h-px bg-gray-200/50"></div>

          <Attributes :attributes="track.attributes" />
        </template>

        <template v-if="transcriptEntries.length">
          <div class="my-[22px] w-full h-px bg-gray-200/50"></div>

          <Transcript :entries="transcriptEntries" :track-index="index" />
        </template>

        <template v-if="track.stems.breakdown.length">
          <div class="my-[22px] w-full h-px bg-gray-200/50"></div>

          <div class="text-[12px] text-gray-500 leading-[20px] mb-1.5">
            Stems ({{ formatNumber(track.stems.breakdown.length) }})
          </div>

          <div class="flex flex-col gap-y-[14px]">
            <Stem
              v-for="stem in track.stems.breakdown"
              :key="stem.name"
              :name="stem.name"
              :url="stem.url"
              :sheet-music="stem.sheetMusic?.preview"
              :track-index="index"
            />
          </div>
        </template>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ChevronDownIcon, PlayIcon, PauseIcon } from '@dacr/icons'
import { Button } from '@dacr/library'
import Attributes from '@/shared/components/Attributes.vue'
import Transcript from '@/features/copyright/components/Transcript.vue'
import Stem from '@/features/copyright/components/Stem.vue'
import type { CopyrightFile } from '@/features/copyright/composables/useCopyrightFile'
import { stemIcon } from '@/features/copyright/utils/stem'
import { formatClockRange } from '@/shared/utils/datetime'
import { formatNumber } from '@/shared/utils/number'
import { computed, onMounted, ref } from 'vue'

type VideoFileAnalysis = Extract<
  NonNullable<CopyrightFile['analysis']>,
  { __typename?: 'VideoAnalysis' }
>
type Track = VideoFileAnalysis['tracks'][number]

const props = defineProps<{
  track: Track
  index: number
}>()

const isExpanded = ref(false)

const transcriptEntries = computed(() => props.track.transcript.preview)
const hasContent = computed(
  () =>
    !!props.track.summary ||
    !!props.track.attributes.length ||
    !!transcriptEntries.value.length ||
    !!props.track.stems.breakdown.length
)

const timeRange = computed(() => {
  const { start, end } = props.track
  if (typeof start !== 'number' || typeof end !== 'number') return null

  return formatClockRange(start, end)
})

const stemCount = computed(() => props.track.stems.breakdown.length)

const stemIcons = computed(() =>
  props.track.stems.breakdown.map((stem) => stemIcon(stem.name))
)

const forceReflow = (node: HTMLElement) => node.scrollHeight

const handleEnter = (element: Element) => {
  const node = element as HTMLElement
  const start = node.style.height ? node.offsetHeight : 0

  node.style.height = `${start}px`
  forceReflow(node)
  node.style.height = `${node.scrollHeight}px`
}

const handleAfterEnter = (element: Element) => {
  ;(element as HTMLElement).style.height = ''
}

const handleLeave = (element: Element) => {
  const node = element as HTMLElement

  node.style.height = `${node.offsetHeight}px`
  forceReflow(node)
  node.style.height = '0px'
}

const el = ref<HTMLElement>()
const wavesurfer = ref<InstanceType<typeof import('wavesurfer.js').default>>()

const isPlaying = ref(false)

const playIcon = computed(() => (isPlaying.value ? PauseIcon : PlayIcon))

const iconStyles = computed(() => ({
  marginLeft: isPlaying.value ? null : '3px',
}))

const handleClick = () => {
  wavesurfer.value?.playPause()

  isPlaying.value = !!wavesurfer.value?.isPlaying()
}

onMounted(async () => {
  if (!el.value) return

  const WaveSurfer = (await import('wavesurfer.js')).default

  wavesurfer.value = WaveSurfer.create({
    container: el.value,
    waveColor: '#AFB5C0',
    progressColor: '#222',
    url: props.track.url,
    height: 40,
    cursorWidth: 0,
  })
})
</script>
